/**
 * HTTPクライアントの純粋ファクトリ。
 * 設定解決やフォールバックは呼び出し側に委ねる。
 */

import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'

/**
 * HTTPクライアント設定
 * すべての値を明示指定すること。
 */
export interface HttpClientConfig {
    baseURL: string
    timeout: number
    headers: Record<string, string>
    withCredentials: boolean
    onUnauthorized: (error: AxiosError) => void
}

/**
 * HTTPクライアントインターフェース
 * Axios インスタンスをそのまま公開する
 */
export type HttpClient = AxiosInstance

/**
 * 純粋なHTTPクライアントを生成する
 * @param config - HTTPクライアント設定
 * @returns Axios 互換のクライアント
 * @throws TypeError baseURL が空または不正な場合
 */
export function createHttpClient(config: HttpClientConfig): HttpClient {
    if (!config.baseURL || typeof config.baseURL !== 'string') {
        throw new TypeError('baseURL は必須です')
    }

    let parsedUrl: URL
    try {
        parsedUrl = new URL(config.baseURL)
    } catch (error) {
        throw new TypeError('baseURL は有効なURLである必要があります')
    }

    if (!parsedUrl.protocol?.startsWith('http')) {
        throw new TypeError('baseURL は http または https を指定してください')
    }

    const instance = axios.create({
        baseURL: config.baseURL,
        timeout: config.timeout,
        headers: config.headers,
        withCredentials: config.withCredentials,
    })

    instance.interceptors.response.use(
        (response: AxiosResponse) => response,
        (error: AxiosError) => {
            if (error.response?.status === 401) {
                config.onUnauthorized(error)
            }
            return Promise.reject(error)
        }
    )

    return instance
}

/**
 * Axios リクエスト設定の型エイリアス
 * 呼び出し側が axios に依存しないよう、export しておく
 */
export type HttpRequestConfig<T = unknown> = AxiosRequestConfig<T>
